import { useCallback, useEffect, useMemo, useState } from "react";
import { apiDeleteWorkspace, apiRenameWorkspace } from "../lib/authApi";
import { usePins } from "../app/context/PinsContext";
import type { WorkspaceSearchSelectArgs } from "./WorkspaceMessageSearchPanel";
import { WorkspaceMessageSearchPanel } from "./WorkspaceMessageSearchPanel";
import type { WorkspaceTopicSelectArgs } from "./WorkspaceTopicList";
import { WorkspaceTopicList } from "./WorkspaceTopicList";
import { MobileEntityHeader, type MobileEntityHeaderMenuItem } from "./MobileEntityHeader";
import { WorkspaceMembersPanel } from "./WorkspaceMembersPanel";

type WorkspaceTopicsScreenProps = {
  workspaceId: string;
  workspaceName: string;
  /** owner | admin | member — from mobile workspace picker */
  workspaceRole?: string;
  canCreateTopics?: boolean;
  topicsRefreshEpoch?: number;
  currentUserId?: string;
  resolveAuthorName?: (authorId: string) => string | null;
  onSelectTopic: (args: WorkspaceTopicSelectArgs) => void;
  onSelectSearchResult: (args: WorkspaceSearchSelectArgs) => void;
  onBack: () => void;
  /** After workspace deleted on server */
  onWorkspaceDeleted?: () => void;
  /** After workspace renamed (update shell labels / sidebar). */
  onWorkspaceRenamed?: (workspaceId: string, newName: string) => void;
  onPinnedTopicIdsChange?: (workspaceId: string, ids: string[]) => void;
};

export function WorkspaceTopicsScreen({
  workspaceId,
  workspaceName,
  workspaceRole,
  canCreateTopics,
  topicsRefreshEpoch = 0,
  currentUserId,
  resolveAuthorName,
  onSelectTopic,
  onSelectSearchResult,
  onBack,
  onWorkspaceDeleted,
  onWorkspaceRenamed,
  onPinnedTopicIdsChange,
}: WorkspaceTopicsScreenProps) {
  const { isPinned, togglePin } = usePins();
  const [searchOpen, setSearchOpen] = useState(false);
  const [createTrigger, setCreateTrigger] = useState(0);
  const [membersOpen, setMembersOpen] = useState(false);

  const handleCreateTopic = useCallback(() => {
    setCreateTrigger((n) => n + 1);
  }, []);

  const handleDeleteWorkspace = useCallback(async () => {
    if (workspaceRole !== "owner") return;
    if (
      !window.confirm(
        `Удалить workspace «${workspaceName}»? Все темы и сообщения будут удалены.`,
      )
    )
      return;
    try {
      await apiDeleteWorkspace(workspaceId);
      onWorkspaceDeleted?.();
    } catch (e: unknown) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
  }, [workspaceId, workspaceName, workspaceRole, onWorkspaceDeleted]);

  const handleRenameWorkspace = useCallback(async () => {
    if (workspaceRole !== "owner" && workspaceRole !== "admin") return;
    const next = window.prompt("Новое имя workspace:", workspaceName)?.trim();
    if (!next || next === workspaceName) return;
    try {
      await apiRenameWorkspace(workspaceId, next);
      onWorkspaceRenamed?.(workspaceId, next);
    } catch (e: unknown) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
  }, [workspaceId, workspaceName, workspaceRole, onWorkspaceRenamed]);

  const overflowItems = useMemo((): MobileEntityHeaderMenuItem[] => {
    const items: MobileEntityHeaderMenuItem[] = [
      {
        id: "search",
        label: "Поиск по workspace",
        onSelect: () => setSearchOpen(true),
      },
    ];
    if (canCreateTopics) {
      items.push({
        id: "add-topic",
        label: "Создать тему",
        onSelect: handleCreateTopic,
      });
    }
    items.push({
      id: "members",
      label: "Участники workspace",
      onSelect: () => setMembersOpen(true),
    });
    if (workspaceRole === "owner" || workspaceRole === "admin") {
      items.push({
        id: "rename-ws",
        label: "Переименовать workspace",
        onSelect: () => void handleRenameWorkspace(),
      });
    }
    items.push({
      id: "pin-ws",
      label: isPinned("workspace", workspaceId) ? "Открепить workspace" : "Закрепить workspace",
      onSelect: () => togglePin("workspace", workspaceId),
    });
    if (workspaceRole === "owner") {
      items.push({
        id: "del-ws",
        label: "Удалить workspace",
        danger: true,
        onSelect: () => void handleDeleteWorkspace(),
      });
    }
    return items;
  }, [
    canCreateTopics,
    workspaceRole,
    workspaceId,
    isPinned,
    togglePin,
    handleCreateTopic,
    handleRenameWorkspace,
    handleDeleteWorkspace,
  ]);

  useEffect(() => {
    setSearchOpen(false);
    setCreateTrigger(0);
    setMembersOpen(false);
  }, [workspaceId]);

  return (
    <div className="chat-shell chat-shell--workspace-topics-screen">
      <MobileEntityHeader onBack={onBack} title={workspaceName} subtitle="Темы workspace" overflowItems={overflowItems} />
      {searchOpen ? (
        <WorkspaceMessageSearchPanel
          workspaceId={workspaceId}
          onClose={() => setSearchOpen(false)}
          onSelectResult={(args) => {
            setSearchOpen(false);
            onSelectSearchResult(args);
          }}
        />
      ) : null}
      <div className="workspace-topics-screen__body">
        <WorkspaceTopicList
          enabled
          workspaceId={workspaceId}
          workspaceName={workspaceName}
          canCreateTopics={canCreateTopics}
          autoSelectFirst={false}
          variant="mobile-screen"
          onSelectTopic={onSelectTopic}
          topicsRefreshEpoch={topicsRefreshEpoch}
          currentUserId={currentUserId}
          resolveAuthorName={resolveAuthorName}
          createTrigger={createTrigger}
          workspaceRole={(workspaceRole === "owner" || workspaceRole === "admin" || workspaceRole === "member"
            ? workspaceRole
            : "member") as "owner" | "admin" | "member"}
          onPinnedTopicIdsChange={onPinnedTopicIdsChange}
        />
      </div>
      {currentUserId ? (
        <WorkspaceMembersPanel
          workspaceId={workspaceId}
          currentUserId={currentUserId}
          open={membersOpen}
          onClose={() => setMembersOpen(false)}
        />
      ) : null}
    </div>
  );
}
